/**
 * CacheConfigContext.tsx
 * 
 * Context per la configurazione dei tempi di cache dell'applicazione Ufficio Stile.
 * Fornisce configurazioni centrali per TanStack Query e permette la gestione dinamica
 * dei tempi di cache per diverse entità.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { cacheService } from '../services/api';
import { ServerCacheConfig } from '../types/cache-types';

// Configurazione di default per i tempi di cache (in millisecondi)
export const DEFAULT_CACHE_TIMES = {
  // Core entities - cache più breve per dati che cambiano frequentemente
  requests: 5 * 60 * 1000, // 5 minuti
  tasks: 3 * 60 * 1000, // 3 minuti
  comments: 2 * 60 * 1000, // 2 minuti
  attachments: 10 * 60 * 1000, // 10 minuti
  
  // User data - cache media
  users: 10 * 60 * 1000, // 10 minuti
  userProfile: 15 * 60 * 1000, // 15 minuti
  
  // Reference data - cache più lunga per dati relativamente statici
  clients: 15 * 60 * 1000, // 15 minuti
  articles: 15 * 60 * 1000, // 15 minuti
  
  // External data - cache più lunga dato che i dati esterni cambiano meno frequentemente
  externalClients: 30 * 60 * 1000, // 30 minuti
  externalArticles: 30 * 60 * 1000, // 30 minuti
  
  // Statistics and analytics - cache lunga per dati pesanti da calcolare
  statistics: 10 * 60 * 1000, // 10 minuti
  dashboard: 5 * 60 * 1000, // 5 minuti
  analytics: 15 * 60 * 1000, // 15 minuti
  
  // Lookup data - cache molto lunga per dati che cambiano raramente
  roles: 60 * 60 * 1000, // 1 ora
  permissions: 60 * 60 * 1000, // 1 ora
  settings: 30 * 60 * 1000, // 30 minuti
  
  // Auth data - cache media per dati di autenticazione
  auth: 15 * 60 * 1000, // 15 minuti
  
  // Cache management - cache per operazioni di cache
  cache: 5 * 60 * 1000, // 5 minuti
  
  // Products - cache per prodotti
  products: 15 * 60 * 1000, // 15 minuti
  
  // Search results - cache breve per risultati di ricerca
  search: 1 * 60 * 1000, // 1 minuto
  autocomplete: 5 * 60 * 1000, // 5 minuti
  
  // File operations - cache media
  uploads: 10 * 60 * 1000, // 10 minuti
  downloads: 5 * 60 * 1000, // 5 minuti
  
  // Notifications - cache breve per notifiche
  notifications: 1 * 60 * 1000, // 1 minuto
  
  // System health - cache breve per dati di sistema
  health: 30 * 1000, // 30 secondi
  metrics: 2 * 60 * 1000, // 2 minuti
};

// Tipo per la configurazione della cache in questo contesto
export interface CacheConfig {
  [key: string]: number;
}

interface CacheConfigContextType {
  config: CacheConfig;
  updateConfig: (newConfig: Partial<CacheConfig>) => void;
  resetConfig: () => void;
  isLoading: boolean;
  error: string | null;
  saveConfig: () => Promise<void>;
  loadConfig: () => Promise<void>;
  getCacheTime: (key: string) => number;
}

const CacheConfigContext = createContext<CacheConfigContextType | undefined>(undefined);

export function CacheConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<CacheConfig>(DEFAULT_CACHE_TIMES);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carica la configurazione cache dal server all'avvio
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // TEMPORANEAMENTE DISABILITATO - Il backend non ha ancora l'endpoint /api/cache/config
      // Utilizziamo la configurazione di default
      console.info('Utilizzo configurazione cache di default (endpoint /api/cache/config non implementato)');
      
      // // Prova a caricare la configurazione dal server
      // const serverConfig = await cacheService.getCacheConfiguration();
      
      // // Filtra solo le proprietà numeriche che sono valide cache times
      // const filteredServerConfig: Partial<CacheConfig> = {};
      // if (serverConfig) {
      //   Object.entries(serverConfig).forEach(([key, value]) => {
      //     if (typeof value === 'number' && key in DEFAULT_CACHE_TIMES) {
      //       filteredServerConfig[key] = value;
      //     }
      //   });
      // }
      
      // // Merge della configurazione del server filtrata con quella di default
      // const mergedConfig = {
      //   ...DEFAULT_CACHE_TIMES,
      //   ...filteredServerConfig
      // };
      
      setConfig(DEFAULT_CACHE_TIMES);
    } catch (err) {
      console.warn('Impossibile caricare la configurazione cache dal server, utilizzo quella di default', err);
      setError('Configurazione cache non disponibile dal server');
      setConfig(DEFAULT_CACHE_TIMES);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = (newConfig: Partial<CacheConfig>) => {
    setConfig(prevConfig => {
      const updated = { ...prevConfig };
      Object.entries(newConfig).forEach(([key, value]) => {
        if (value !== undefined) {
          updated[key] = value;
        }
      });
      return updated;
    });
  };

  const resetConfig = () => {
    setConfig(DEFAULT_CACHE_TIMES);
    setError(null);
  };

  const saveConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Qui potremmo mappare i valori per il server se necessario
      const serverConfig = { ...config };
      await cacheService.updateCacheConfiguration(serverConfig);
      
      // Salva anche nel localStorage come backup
      localStorage.setItem('us-cache-config', JSON.stringify(config));
    } catch (err) {
      console.error('Errore nel salvataggio della configurazione cache:', err);
      setError('Errore nel salvataggio della configurazione cache');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Utility per salvare configurazione nel localStorage
  useEffect(() => {
    if (!isLoading && !error) {
      localStorage.setItem('us-cache-config', JSON.stringify(config));
    }
  }, [config, isLoading, error]);

  // Carica configurazione dal localStorage se disponibile
  useEffect(() => {
    const savedConfig = localStorage.getItem('us-cache-config');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(prevConfig => {
          const updated = { ...prevConfig };
          Object.entries(parsedConfig).forEach(([key, value]) => {
            if (typeof value === 'number') {
              updated[key] = value;
            }
          });
          return updated;
        });
      } catch (err) {
        console.error('Errore nel parsing della configurazione cache salvata:', err);
      }
    }
  }, []);

  const getCacheTime = (key: string): number => {
    return config[key] || DEFAULT_CACHE_TIMES[key as keyof typeof DEFAULT_CACHE_TIMES] || 5 * 60 * 1000; // 5 minuti di default
  };

  const value: CacheConfigContextType = {
    config,
    updateConfig,
    resetConfig,
    isLoading,
    error,
    saveConfig,
    loadConfig,
    getCacheTime
  };

  return (
    <CacheConfigContext.Provider value={value}>
      {children}
    </CacheConfigContext.Provider>
  );
}

export function useCacheConfig() {
  const context = useContext(CacheConfigContext);
  if (context === undefined) {
    // Invece di lanciare un errore, restituiamo una configurazione di fallback
    console.warn('useCacheConfig utilizzato fuori dal CacheConfigProvider, utilizzo configurazione di default');
    return {
      config: DEFAULT_CACHE_TIMES,
      updateConfig: () => {},
      resetConfig: () => {},
      isLoading: false,
      error: null,
      saveConfig: async () => {},
      loadConfig: async () => {},
      getCacheTime: (key: string) => DEFAULT_CACHE_TIMES[key as keyof typeof DEFAULT_CACHE_TIMES] || 5 * 60 * 1000
    };
  }
  return context;
}

// Hook per ottenere un tempo di cache specifico
export function useCacheTime(key: keyof typeof DEFAULT_CACHE_TIMES): number {
  const { config } = useCacheConfig();
  return config[key] || DEFAULT_CACHE_TIMES[key];
}

// Hook per configurazioni di cache multiple
export function useCacheTimes<T extends string>(
  keys: T[]
): Record<T, number> {
  const { getCacheTime } = useCacheConfig();
  
  return keys.reduce((acc, key) => {
    acc[key] = getCacheTime(key);
    return acc;
  }, {} as Record<T, number>);
}

// Utility per convertire millisecondi in formato leggibile
export function formatCacheTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

// Utility per validare un tempo di cache
export function isValidCacheTime(ms: number): boolean {
  return ms > 0 && ms <= 24 * 60 * 60 * 1000; // Massimo 24 ore
}

// Utility per ottenere suggerimenti sui tempi di cache
export function getCacheTimeSuggestions(entityType: string): number[] {
  const suggestions = {
    'fast-changing': [30 * 1000, 1 * 60 * 1000, 2 * 60 * 1000, 5 * 60 * 1000], // 30s, 1m, 2m, 5m
    'medium-changing': [5 * 60 * 1000, 10 * 60 * 1000, 15 * 60 * 1000, 30 * 60 * 1000], // 5m, 10m, 15m, 30m
    'slow-changing': [15 * 60 * 1000, 30 * 60 * 1000, 60 * 60 * 1000, 2 * 60 * 60 * 1000], // 15m, 30m, 1h, 2h
    'static': [60 * 60 * 1000, 2 * 60 * 60 * 1000, 6 * 60 * 60 * 1000, 24 * 60 * 60 * 1000], // 1h, 2h, 6h, 24h
  };
  
  // Logica per determinare il tipo di entità
  if (['notifications', 'health', 'search'].includes(entityType)) {
    return suggestions['fast-changing'];
  } else if (['requests', 'tasks', 'comments', 'dashboard'].includes(entityType)) {
    return suggestions['medium-changing'];
  } else if (['users', 'clients', 'articles', 'externalClients', 'externalArticles'].includes(entityType)) {
    return suggestions['slow-changing'];
  } else {
    return suggestions['static'];
  }
}

// Export del provider e degli hook
export default CacheConfigProvider;
